import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import { useDealStore } from '@/store/dealStore';
import { useToast } from '@/components/ui/use-toast';
import { Deal, DealFormData, CUSTOMER_TYPES, CUSTOMER_TYPE_LABELS, CustomerType, PRODUCT_SKUS } from '@/types/crm';

interface DealFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deal?: Deal | null;
  defaultCompanyId?: string;
  defaultContactId?: string;
  defaultCompanyName?: string;
  defaultContactName?: string;
}

export function DealFormDialog({ open, onOpenChange, deal, defaultCompanyId, defaultContactId, defaultCompanyName, defaultContactName }: DealFormDialogProps) {
  const { createDeal, updateDeal, stages, fetchStages, isLoading } = useDealStore();
  const { toast } = useToast();
  const isEditing = !!deal;

  const [formData, setFormData] = useState<DealFormData>({
    title: '',
    value: '' as unknown as number,
    currency: 'USD',
    stage_id: '',
    company_id: defaultCompanyId || '',
    contact_id: defaultContactId || '',
    company_name: defaultCompanyName || '',
    contact_name: defaultContactName || '',
    expected_close_date: '',
    probability: '' as unknown as number,
    customer_type: undefined,
    product_skus: [],
    description: '',
  });

  useEffect(() => {
    if (open) {
      if (stages.length === 0) fetchStages();
    }
  }, [open, stages.length, fetchStages]);

  useEffect(() => {
    if (deal) {
      setFormData({
        title: deal.title,
        value: deal.value,
        currency: deal.currency,
        stage_id: deal.stage_id,
        company_id: deal.company_id || '',
        contact_id: deal.contact_id || '',
        company_name: deal.company_name || '',
        contact_name: deal.contact_name || '',
        expected_close_date: deal.expected_close_date ? deal.expected_close_date.split('T')[0] : '',
        probability: deal.probability,
        customer_type: deal.customer_type,
        product_skus: deal.product_skus || [],
        description: deal.description || '',
      });
    } else {
      setFormData({
        title: '',
        value: '' as unknown as number,
        currency: 'USD',
        stage_id: stages[0]?.id || '',
        company_id: defaultCompanyId || '',
        contact_id: defaultContactId || '',
        company_name: defaultCompanyName || '',
        contact_name: defaultContactName || '',
        expected_close_date: '',
        probability: '' as unknown as number,
        customer_type: undefined,
        product_skus: [],
        description: '',
      });
    }
  }, [deal, open, defaultCompanyId, defaultContactId, defaultCompanyName, defaultContactName, stages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const selectedStage = stages.find(s => s.id === formData.stage_id);
      const submitData: Record<string, unknown> = {
        title: formData.title,
        stage_id: formData.stage_id,
        stage: selectedStage?.name || null,
        value: typeof formData.value === 'number' && !isNaN(formData.value) ? formData.value : 0,
        currency: formData.currency,
        probability: typeof formData.probability === 'number' && !isNaN(formData.probability) ? formData.probability : 0,
        description: formData.description || null,
        expected_close_date: formData.expected_close_date || null,
        company_id: formData.company_id || null,
        contact_id: formData.contact_id || null,
        company_name: formData.company_name || null,
        contact_name: formData.contact_name || null,
        customer_type: formData.customer_type || null,
        product_skus: formData.product_skus?.length ? formData.product_skus : null,
      };

      if (isEditing && deal) {
        await updateDeal(deal.id, submitData);
        toast({ title: 'Deal updated successfully' });
      } else {
        await createDeal(submitData as unknown as DealFormData);
        toast({ title: 'Deal created successfully' });
      }
      onOpenChange(false);
    } catch (error) {
      toast({ title: 'Error', description: String(error), variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Deal' : 'New Deal'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Deal Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="value">Value *</Label>
              <Input
                id="value"
                type="number"
                min="0"
                step="0.01"
                value={formData.value === ('' as unknown as number) ? '' : formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value === '' ? ('' as unknown as number) : parseFloat(e.target.value) })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select value={formData.currency} onValueChange={(value) => setFormData({ ...formData, currency: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                  <SelectItem value="INR">INR</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Stage *</Label>
            <Select value={formData.stage_id} onValueChange={(value) => setFormData({ ...formData, stage_id: value })}>
              <SelectTrigger><SelectValue placeholder="Select stage" /></SelectTrigger>
              <SelectContent>
                {stages.map((stage) => (
                  <SelectItem key={stage.id} value={stage.id}>{stage.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="company_name">Company Name</Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                placeholder="Enter company name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_name">Contact Name</Label>
              <Input
                id="contact_name"
                value={formData.contact_name}
                onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                placeholder="Enter contact name"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Customer Type</Label>
            <Select
              value={formData.customer_type || ''}
              onValueChange={(value) => setFormData({ ...formData, customer_type: value as CustomerType })}
            >
              <SelectTrigger><SelectValue placeholder="Select customer type" /></SelectTrigger>
              <SelectContent>
                {CUSTOMER_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>{CUSTOMER_TYPE_LABELS[type]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Product / SKU</Label>
            <MultiSelect
              options={PRODUCT_SKUS}
              selected={formData.product_skus || []}
              onChange={(selected) => setFormData({ ...formData, product_skus: selected })}
              placeholder="Select products..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="expected_close_date">Expected Close Date</Label>
              <Input
                id="expected_close_date"
                type="date"
                value={formData.expected_close_date}
                onChange={(e) => setFormData({ ...formData, expected_close_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="probability">Probability (%)</Label>
              <Input
                id="probability"
                type="number"
                min="0"
                max="100"
                value={formData.probability === ('' as unknown as number) ? '' : formData.probability}
                onChange={(e) => setFormData({ ...formData, probability: e.target.value === '' ? ('' as unknown as number) : parseInt(e.target.value) })}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={isLoading}>{isLoading ? 'Saving...' : isEditing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
